import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';
import { ErrorState, LoadingState } from '@/shared/ui';
import { EpisodeDivider, formatDayLong, isSameDay } from '@/shared/ui/conversation';

import { mergeTimeline, useConversationLatestMessages, useConversationMessages } from '../../hooks';
import { findQuoted, resolveCallbackLabel } from '../../model/messageDisplay';
import type { ConversationEpisodeSummary, ConversationMessageView } from '../../model/types';
import { MessageBubble } from './MessageBubble';

export interface TimelineProps {
  conversationId: string;
  currentEpisodeId?: string | null;
  episodesById: Map<string, ConversationEpisodeSummary>;
  canViewPii: boolean;
  canReply: boolean;
  onReplyToThis?: (message: ConversationMessageView) => void;
  /** The signed-in operator — decides the "you" variant of an operator row's who-label (design §13.2). */
  meId?: string | null;
}

const NEAR_BOTTOM_THRESHOLD_PX = 200;

/**
 * The cross-channel timeline (design §7.4/§11.6): the live tail merged with the keyset-paged older
 * pages, grouped by episode, with day separators, "Load earlier messages", and a floating "N new
 * messages" chip while scrolled up. Renders one {@link MessageBubble} per row.
 */
export function Timeline({
  conversationId,
  currentEpisodeId,
  episodesById,
  canViewPii,
  canReply,
  onReplyToThis,
  meId,
}: TimelineProps) {
  const { t } = useTranslation('conversations');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [newSinceScroll, setNewSinceScroll] = useState(0);
  const previousFirstId = useRef<string | null>(null);
  const previousLastId = useRef<string | null>(null);
  const previousScrollHeight = useRef<number | null>(null);

  const latest = useConversationLatestMessages(conversationId);
  const tailNextBefore = latest.data?.nextBefore ?? null;
  const olderQueryEnabled = tailNextBefore !== null;
  const older = useConversationMessages(conversationId, tailNextBefore, {
    enabled: olderQueryEnabled,
  });

  const rows = useMemo(() => mergeTimeline(older.data, latest.data), [older.data, latest.data]);

  const beginningReached =
    !olderQueryEnabled || (older.data !== undefined && older.hasNextPage === false);
  const olderFirstLoad = olderQueryEnabled && older.data === undefined;

  // Scroll to the bottom exactly once, when the first page of rows is available.
  useEffect(() => {
    if (!hasOpenedOnce && rows.length > 0 && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setHasOpenedOnce(true);
    }
  }, [hasOpenedOnce, rows.length]);

  // Scroll anchoring (design §11.6) — distinguishes a PREPEND (older page loaded: the first row's id
  // changes, keep the previously-topmost row in view by growing scrollTop by the added height) from an
  // APPEND (a poll brought newer rows: the last row's id changes, no adjustment needed — content below
  // the fold does not move the viewport on its own).
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || rows.length === 0) {
      return;
    }
    const firstId = rows[0].id;
    const lastId = rows[rows.length - 1].id;
    const isPrepend = previousFirstId.current !== null && firstId !== previousFirstId.current;
    const isAppend = previousLastId.current !== null && lastId !== previousLastId.current;

    if (isPrepend && !isAppend && previousScrollHeight.current !== null) {
      const grew = container.scrollHeight - previousScrollHeight.current;
      if (grew > 0) {
        container.scrollTop += grew;
      }
    } else if (isAppend && isNearBottom) {
      container.scrollTop = container.scrollHeight;
    } else if (isAppend && !isNearBottom) {
      setNewSinceScroll((n) => n + 1);
    }

    previousFirstId.current = firstId;
    previousLastId.current = lastId;
    previousScrollHeight.current = container.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts only to row identity changes
  }, [rows]);

  function handleScroll() {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const nowNearBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
    setIsNearBottom(nowNearBottom);
    if (nowNearBottom) {
      setNewSinceScroll(0);
    }
  }

  function scrollToBottom() {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
    setNewSinceScroll(0);
    setIsNearBottom(true);
  }

  function jumpToMessage(messageId: string) {
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    el?.scrollIntoView({ block: 'center' });
  }

  if (latest.isLoading) {
    return <LoadingState fullHeight={false} label={t('timeline.loading')} />;
  }
  if (latest.isError) {
    return (
      <ErrorState
        title={t('list.loadError')}
        message={latest.error?.message}
        onRetry={() => void latest.refetch()}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('timeline.empty')}
        </Typography>
      </Box>
    );
  }

  let lastEpisodeId: string | null | undefined;
  let lastDay: string | null = null;
  const items: ReactNode[] = [];

  rows.forEach((row, index) => {
    const isNewDay = !lastDay || !isSameDay(row.createdAt, lastDay);
    const startsNewEpisode = row.episodeId != null && row.episodeId !== lastEpisodeId;

    if (startsNewEpisode && row.episodeId) {
      const episode = episodesById.get(row.episodeId);
      items.push(
        <EpisodeDivider
          key={`episode-${row.episodeId}`}
          startedAt={episode?.startedAt ?? row.createdAt}
          topic={episode?.topic}
          current={row.episodeId === currentEpisodeId}
        />,
      );
    }
    if (isNewDay) {
      items.push(
        <Divider key={`day-${row.id}`} textAlign="center">
          <Chip size="small" label={formatDayLong(row.createdAt, getIntlLocale())} />
        </Divider>,
      );
      lastDay = row.createdAt;
    }
    lastEpisodeId = row.episodeId ?? lastEpisodeId;

    const quoted = findQuoted(rows, row.replyToMessageId);
    const callbackLabel =
      row.kind === 'CALLBACK' ? resolveCallbackLabel(rows, index, row.text ?? null) : undefined;

    items.push(
      <Box key={row.id} data-message-id={row.id}>
        <MessageBubble
          message={row}
          canViewPii={canViewPii}
          canReply={canReply}
          quoted={quoted}
          callbackLabel={callbackLabel}
          onReplyToThis={onReplyToThis}
          onJumpToMessage={jumpToMessage}
          meId={meId}
        />
      </Box>,
    );
  });

  return (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        role="log"
        aria-relevant="additions"
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1 }}
      >
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {olderFirstLoad ? (
            <CircularProgress size={18} />
          ) : beginningReached ? (
            <Typography variant="caption" color="text.disabled">
              {t('timeline.beginning')}
            </Typography>
          ) : older.isError ? (
            <Button size="small" onClick={() => void older.fetchNextPage()}>
              {t('timeline.loadEarlierError')}
            </Button>
          ) : (
            <Button
              size="small"
              variant="text"
              startIcon={
                older.isFetchingNextPage ? (
                  <CircularProgress size={14} />
                ) : (
                  <ExpandLessRoundedIcon />
                )
              }
              disabled={older.isFetchingNextPage}
              onClick={() => void older.fetchNextPage()}
            >
              {t('timeline.loadEarlier')}
            </Button>
          )}
        </Box>

        <Stack spacing={1.25}>{items}</Stack>
      </Box>

      {!isNearBottom && newSinceScroll > 0 ? (
        <Chip
          color="primary"
          onClick={scrollToBottom}
          label={t(
            newSinceScroll === 1 ? 'timeline.newMessages_one' : 'timeline.newMessages_other',
            {
              count: newSinceScroll,
            },
          )}
          sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)' }}
        />
      ) : null}
    </Box>
  );
}
